import store from "@/redux/store";
import { api as vendorApi } from "./vendorApi";
import { getConversationId, getPayloadChatId, getPayloadSenderId, isKnownCustomerCareConversation, readMarketplaceConversation } from "./conversationBoundary";
import type { MarketplaceConversation } from "./conversationBoundary";
import { messageBelongsToChat, readAcknowledgement, readHistory, validChatPreview } from "@/components/VendorInfo/chat/vendorMessageState";

const API_BASE_URL = "https://ilosiwaju-mbaay-2025.com/api/v1/chat";
// Every chat request uses the existing vendor authentication and refresh flow.
export const api = vendorApi;
const admitted = new Map<string, Map<string, MarketplaceConversation>>();
const listRequests = new Map<string, { sequence: number; promise: Promise<MarketplaceConversation[]> }>();
let listSequence = 0;
const account = () => {
  const state = store.getState().vendor;
  if (!state.token || !state.vendor?._id) throw new Error("Sign in as a vendor to use marketplace messages.");
  return { role: "vendor" as const, id: state.vendor._id };
};
const assertSameAccount = (accountId: string) => {
  if (account().id !== accountId) throw new Error("The vendor session changed. Please reopen messages.");
};
const assertSuccess = (payload: unknown) => {
  if (payload !== null && typeof payload === "object" && "success" in payload && payload.success === false) {
    const message = "message" in payload && typeof payload.message === "string" ? payload.message : "The chat request could not be completed.";
    throw new Error(message);
  }
};
export const getUserChats = async (_token?: string | null): Promise<MarketplaceConversation[]> => {
  void _token;
  const current = account();
  const sequence = ++listSequence;
  const promise = (async () => {
    const response = await api.get(`${API_BASE_URL}/chats`);
    assertSameAccount(current.id);
    // A slower list request cannot restore removed threads over a newer snapshot.
    const latest = listRequests.get(current.id);
    if (latest && latest.sequence !== sequence) return latest.promise;
    assertSuccess(response.data);
    if (!Array.isArray(response.data?.chats)) throw new Error("The conversations response is missing its chats list.");
    const conversations = response.data.chats.flatMap((raw: unknown) => {
      const chat = readMarketplaceConversation(raw, current);
      if (!chat) return [];
      return [{ ...chat, raw: { ...chat.raw, lastMessage: validChatPreview(chat.raw.lastMessage, chat._id, chat.participants.map((participant) => participant.id)) ? chat.raw.lastMessage : null } }];
    });
    admitted.set(current.id, new Map(conversations.map((chat: MarketplaceConversation) => [chat._id, chat])));
    return conversations;
  })();
  listRequests.set(current.id, { sequence, promise });
  return promise;
};
const requireMarketplaceChat = async (chatId: string) => {
  const current = account();
  if (!chatId || isKnownCustomerCareConversation(current, chatId)) throw new Error("This conversation belongs to Customer Care.");
  if (!admitted.get(current.id)?.has(chatId)) await getUserChats();
  assertSameAccount(current.id);
  const conversation = admitted.get(current.id)?.get(chatId);
  if (!conversation || !readMarketplaceConversation(conversation.raw, current)) throw new Error("This marketplace conversation is unavailable.");
  return current;
};
const guardAcknowledgement = (payload: unknown, chatId: string, accountId: string) => {
  const message = readAcknowledgement(payload);
  const conversation = admitted.get(accountId)?.get(chatId);
  if (!conversation || !readMarketplaceConversation(conversation.raw, { role: "vendor", id: accountId })) throw new Error("This conversation is no longer a marketplace conversation.");
  if (!message) return null;
  const room = getPayloadChatId(message); const sender = getPayloadSenderId(message);
  if (message.isCustomerCareChat === true || (room && room !== chatId) || (sender && sender !== accountId)) throw new Error("The server returned a message from a different conversation or sender.");
  return message;
};
export const create_or_get_chat = async (data: { receiverId: string }, _token?: string | null): Promise<MarketplaceConversation> => {
  void _token;
  const current = account();
  if (!data.receiverId || data.receiverId === current.id) throw new Error("Choose a customer to start a conversation.");
  const response = await api.post(`${API_BASE_URL}/create_or_get_chat`, data);
  assertSameAccount(current.id);
  assertSuccess(response.data);
  const raw = response.data?.chat;
  const parsed = readMarketplaceConversation(raw, current);
  const id = parsed?._id || getConversationId(raw) || getConversationId({ _id: response.data?.chatId });
  const chats = await getUserChats();
  const chat = chats.find((item) => item._id === id) || parsed;
  if (!chat) throw new Error("The server did not return a customer/vendor conversation. Please refresh your inbox.");
  if (chat.counterpart.id !== data.receiverId) throw new Error("The server returned a different customer than the one selected.");
  const known = admitted.get(current.id) || new Map<string, MarketplaceConversation>();
  known.set(chat._id, chat);
  admitted.set(current.id, known);
  return chat;
};
export const getChatMessages = async (chatId: string) => {
  const current = await requireMarketplaceChat(chatId);
  const response = await api.get(`${API_BASE_URL}/chat/${chatId}/messages`);
  assertSameAccount(current.id);
  assertSuccess(response.data);
  await requireMarketplaceChat(chatId);
  if (response.data?.isCustomerCareChat === true) throw new Error("The server returned Customer Care history for a marketplace conversation.");
  const history = readHistory(response.data);
  const participants = admitted.get(current.id)?.get(chatId)?.participants.map((participant) => participant.id) || [];
  return { ...history, messages: history.messages.filter((message) => messageBelongsToChat(message, chatId, participants)) };
};
export const sendMessage = async (chatId: string, data: { content: string; replyTo?: string }, _token?: string | null) => {
  void _token;
  const current = await requireMarketplaceChat(chatId);
  const response = await api.post(`${API_BASE_URL}/chat/${chatId}/message`, data);
  assertSameAccount(current.id);
  assertSuccess(response.data);
  if (response.data?.isCustomerCareChat === true) throw new Error("The server returned Customer Care data for a marketplace request.");
  return guardAcknowledgement(response.data, chatId, current.id);
};
export const editMessage = async (messageId: string, data: { text: string }, _token?: string | null, chatId?: string) => {
  void _token;
  const current = await requireMarketplaceChat(chatId || "");
  if (messageId.startsWith("opt-")) throw new Error("Retry this message before editing it.");
  const response = await api.patch(`${API_BASE_URL}/edit/${messageId}`, data);
  assertSameAccount(current.id);
  assertSuccess(response.data);
  if (response.data?.isCustomerCareChat === true) throw new Error("The server returned Customer Care data for a marketplace request.");
  return guardAcknowledgement(response.data, chatId!, current.id);
};
export const deleteMessage = async (messageId: string, _token?: string | null, chatId?: string) => {
  void _token;
  const current = await requireMarketplaceChat(chatId || "");
  if (messageId.startsWith("opt-")) throw new Error("This message has not been sent.");
  const response = await api.delete(`${API_BASE_URL}/delete/${messageId}`);
  assertSameAccount(current.id);
  assertSuccess(response.data);
  if (response.data?.isCustomerCareChat === true) throw new Error("The server returned Customer Care data for a marketplace request.");
  return response.data;
};
export type MediaFiles = { images?: File[]; video?: File; documents?: File[] };
export const sendMediaMessage = async (chatId: string, formData: FormData, _token?: string | null, onUploadProgress?: (percent: number) => void) => {
  const current = await requireMarketplaceChat(chatId);
  const response = await api.post(`${API_BASE_URL}/chat/${chatId}/send_media_message`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (event) => { if (event.total) onUploadProgress?.(Math.round(event.loaded * 100 / event.total)); },
  });
  assertSameAccount(current.id);
  assertSuccess(response.data);
  if (response.data?.isCustomerCareChat === true) throw new Error("The server returned Customer Care data for a marketplace request.");
  return guardAcknowledgement(response.data, chatId, current.id);
};
export const prepareMediaFormData = (files: MediaFiles, content?: string, replyTo?: string): FormData => {
  if ((files.images?.length || 0) > 5 || (files.documents?.length || 0) > 5) throw new Error("Choose up to five images or documents per message.");
  const formData = new FormData();
  if (content?.trim()) formData.append("content", content.trim());
  if (replyTo) formData.append("replyTo", replyTo);
  files.images?.forEach((file) => formData.append("images", file));
  files.documents?.forEach((file) => formData.append("documents", file));
  if (files.video) formData.append("video", files.video);
  return formData;
};
export const getUnreadChatCount = async (userId: string) => {
  const current = account();
  if (userId !== current.id) throw new Error("Unread counts must belong to the signed-in vendor.");
  const chats = await getUserChats();
  const response = await api.get(`${API_BASE_URL}/get_unread_chat_count/${userId}`);
  assertSameAccount(current.id);
  assertSuccess(response.data);
  const allowed = new Set(chats.filter((chat) => Boolean(readMarketplaceConversation(chat.raw, current))).map((chat) => chat._id));
  const items = response.data?.data;
  if (!Array.isArray(items)) throw new Error("The server must return unread counts by conversation to keep Customer Care separate.");
  const counts: Record<string, number> = {};
  for (const item of items) {
    const count = Number(item?.unreadCount);
    if (allowed.has(item?.chatId)) counts[item.chatId] = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  }
  return { count: Object.values(counts).reduce((sum, value) => sum + value, 0), chats: counts };
};
export const markChatAsRead = async (chatId: string, userId: string) => {
  const current = await requireMarketplaceChat(chatId);
  if (userId !== current.id) throw new Error("Read receipts must belong to the signed-in vendor.");
  const response = await api.patch(`${API_BASE_URL}/mark_chat_as_read/${chatId}/${userId}`);
  assertSameAccount(current.id);
  assertSuccess(response.data);
  return response.data;
};
